import React, { Fragment } from "react";
import PropTypes from "prop-types";
import filter from "../../assets/images/cuida_filter-outline.png";
import trier from "../../assets/images/mdi_sort.png";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
  useRowSelect,
} from "react-table";
import { Table, Row, Col, CardBody } from "reactstrap";
import { DefaultColumnFilter } from "../filters";
import { ContactsGlobalFilter } from "../GlobalSearchFilter";
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  FilterOutlined,
  SearchOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { Empty, Spin, Button, Input } from "antd";
import "./TableContainer.css";
import TableSkeleton from "./tableSkeleton";

function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isUsersFilter,
  handleUserClick,
  HeaderTable,
  activeTab,
  children,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);
  return (
    <CardBody className="p-0 w-100">
      <form className="w-100">
        <div className="d-flex align-items-center justify-content-between w-100 mb-3 gap-2">
          <div className="d-flex align-items-center gap-2 w-30">
            <Input
              prefix={<SearchOutlined style={{ color: "#9F9F9F" }} />}
              placeholder={`Rechercher dans ${count} entrées ...`}
              value={value || ""}
              onChange={(e) => {
                setValue(e.target.value);
                onChange(e.target.value);
              }}
              style={{ width: "100%", height: 33 }}
              disabled={count > 0 ? false : true}
            />
          </div>
          <div className="w-70 gap-2">{children}</div>
        </div>
      </form>
    </CardBody>
  );
}
const TableContainer = ({
  columns,
  data,
  isGlobalSearch,
  isPaginationButton = true,
  isGlobalFilter,
  isUsersFilter,
  handleUserClick,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  HeaderTable,
  isLoading,
  isError,
  error,
  activeTab,
  children,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        selectedRowIds: 0,
        sortBy: [{ desc: true }],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
  );

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " " : "") : "";
  };

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };
  const onChangeInInput = (event) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  return (
    <div className="table-wrapper-min">
      {!isLoading && !isError && isGlobalFilter && (
        <Row>
          <Col lg={12}>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={state.globalFilter}
              setGlobalFilter={setGlobalFilter}
              isUsersFilter={isUsersFilter}
              handleUserClick={handleUserClick}
              HeaderTable={HeaderTable}
              activeTab={activeTab}
              children={children}
            />
          </Col>
        </Row>
      )}

      <div className="table-wrapper-arrounded">
        <Table hover {...getTableProps()} className={tableClass}>
          <thead className={theadClass}>
            {headerGroups.map((headerGroup) => (
              <tr
                className={trClass}
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className="table-header-title"
                    {...column.getSortByToggleProps()}
                  >
                    {column.render("Header")}
                    {generateSortingIndicator(column)}
                    {column.isSorted ? (column.isSortedDesc ? "" : " 🔼") : ""}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="d-flex justify-content-center align-items-center py-4">
                    <TableSkeleton columns={columns.length} />
                  </div>
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="text-danger text-center py-4">
                    Erreur : {error || "Une erreur est survenue."}
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="text-center py-4">
                    <Empty description="Aucun résultat trouvé" />
                  </div>
                </td>
              </tr>
            ) : (
              page.map((row) => {
                prepareRow(row);
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr>
                      {row.cells.map((cell) => (
                        <td key={cell.id} {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                );
              })
            )}
          </tbody>
        </Table>
        <Row className="d-flex justify-content-between align-items-center ms-1 me-1 mt-3 mb-3 flex-wrap">
          {isGlobalSearch && (
            <Col xs="12" md="auto" className="mb-2 mb-md-0">
              <select
                className="form-select"
                value={pageSize}
                onChange={onChangeInSelect}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    Afficher {pageSize} lignes
                  </option>
                ))}
              </select>
            </Col>
          )}

          {isPaginationButton && (
            <Col xs="12" md="auto" className="text-md-end">
              <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-md-end mb-0 d-none d-md-flex flex-wrap">
                <li
                  className={
                    !canPreviousPage ? "page-item disabled" : "page-item"
                  }
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={previousPage}
                    title="Précédent"
                  >
                    <DoubleLeftOutlined />
                  </button>
                </li>

                {(() => {
                  const maxVisiblePages = 5;
                  const totalPages = pageOptions.length;
                  const half = Math.floor(maxVisiblePages / 2);

                  let start = Math.max(0, pageIndex - half);
                  let end = start + maxVisiblePages;

                  if (end > totalPages) {
                    end = totalPages;
                    start = Math.max(0, end - maxVisiblePages);
                  }

                  const visiblePages = pageOptions.slice(start, end);

                  return visiblePages.map((item) => (
                    <li className="page-item" key={item}>
                      <button
                        type="button"
                        className={
                          pageIndex === item ? "page-link active" : "page-link"
                        }
                        onClick={() => gotoPage(item)}
                      >
                        {item + 1}
                      </button>
                    </li>
                  ));
                })()}

                <li
                  className={!canNextPage ? "page-item disabled" : "page-item"}
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={nextPage}
                    title="Suivant"
                  >
                    <DoubleRightOutlined />
                  </button>
                </li>
              </ul>

              <ul className="pagination pagination-separated pagination-md justify-content-center mb-0 d-flex d-md-none flex-wrap">
                <li
                  className={
                    !canPreviousPage ? "page-item disabled" : "page-item"
                  }
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={previousPage}
                    title="Précédent"
                  >
                    <DoubleLeftOutlined />
                  </button>
                </li>

                {(() => {
                  const maxVisiblePages = 5;
                  const totalPages = pageOptions.length;
                  const half = Math.floor(maxVisiblePages / 2);

                  let start = Math.max(0, pageIndex - half);
                  let end = start + maxVisiblePages;

                  if (end > totalPages) {
                    end = totalPages;
                    start = Math.max(0, end - maxVisiblePages);
                  }

                  const visiblePages = pageOptions.slice(start, end);

                  return visiblePages.map((item) => (
                    <li className="page-item" key={item}>
                      <button
                        type="button"
                        className={
                          pageIndex === item ? "page-link active" : "page-link"
                        }
                        onClick={() => gotoPage(item)}
                      >
                        {item + 1}
                      </button>
                    </li>
                  ));
                })()}

                <li
                  className={!canNextPage ? "page-item disabled" : "page-item"}
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={nextPage}
                    title="Suivant"
                  >
                    <DoubleRightOutlined />
                  </button>
                </li>
              </ul>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default TableContainer;
